import React from "react";
import propTypes from "prop-types";
import classnames from "classnames";
import Label from "./Label";
import ErrorsForField from "../form/ErrorsForField";

import { useField } from "../form/Form";

import "./TextArea.scss";
import { createDataAttributes } from "../../utilities";

const TextArea = ({
  id,
  name,
  label,
  labelElement,
  value,
  className,
  disabled,
  onChange,
  onBlur,
  onKeyDown,
  onValidate,
  required,
  hideLabel,
  noLabel,
  fieldErrors,
  hideErrors,
  requiredError,
  dataAttrib,
  dataWrapperAttrib,
  ...rest
}) => {
  useField({
    name,
    validation: { required, requiredError },
    onValidate,
    label
  });

  const containerClasses = classnames(
    "uikit__textarea-wrapper",
    "uikit__form-item",
    className,
    {
      "uikit__hide-label": hideLabel,
      "uikit__form__hide-errors": hideErrors
    }
  );
  const inputClass = classnames("uikit__textarea", {
    "has-error": !!fieldErrors?.length,
    "is-required": required,
    "has-value": !!value
  });

  const handleChange = event => {
    onChange({
      event,
      field: name,
      value: event.currentTarget.value
    });
  };

  const handleBlur = event => {
    onBlur({
      event,
      field: name,
      value: String(event.currentTarget.value).trim()
    });
  };

  const handleKeyDown = event => {
    if (onKeyDown) {
      onKeyDown({ event, value: event.currentTarget.value });
    }
  };

  return (
    <div
      className={containerClasses}
      {...createDataAttributes(dataWrapperAttrib)}
    >
      {!noLabel && (
        <Label
          htmlFor={id}
          label={label}
          labelElement={labelElement}
          required={required}
        />
      )}
      <textarea
        type="text"
        id={id}
        name={name}
        value={value}
        className={inputClass}
        onChange={handleChange}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        disabled={disabled}
        {...createDataAttributes(dataAttrib)}
        {...rest}
        aria-describedby={`errors_for_${id}`}
      />
      {!hideErrors && !!fieldErrors?.length && (
        <ErrorsForField id={`errors_for_${id}`} errors={fieldErrors} />
      )}
    </div>
  );
};

TextArea.propTypes = {
  //global form props
  className: propTypes.string,
  disabled: propTypes.bool,

  id: propTypes.string.isRequired,
  label: propTypes.oneOfType([
    propTypes.string,
    propTypes.object,
    propTypes.element,
    propTypes.func
  ]),
  labelElement: propTypes.element,
  name: propTypes.string.isRequired,
  onBlur: propTypes.func.isRequired,
  onChange: propTypes.func.isRequired,
  onKeyDown: propTypes.func,
  onValidate: propTypes.func.isRequired,
  required: propTypes.bool,
  value: propTypes.string.isRequired,
  hideLabel: propTypes.bool,
  noLabel: propTypes.bool,
  requiredError: propTypes.string,

  fieldErrors: propTypes.array,
  hideErrors: propTypes.bool,

  //component-specific
  maxLength: propTypes.number,
  componentname: propTypes.string,
  dataAttrib: propTypes.arrayOf(Object),
  dataWrapperAttrib: propTypes.arrayOf(Object)
};

TextArea.defaultProps = {
  required: false,
  hideErrors: false,
  componentname: "TextArea"
};

export default TextArea;
